import {
  BROWSE_FILTER_ASSET_TYPE_NAMES,
  browseSpokenPhrasesForType,
  isGenericOfficeSearchPhrase,
  isOfficeFamilySearchPhrase,
} from '../constants/filterableAssetTypes';
import { ISRAEL_COUNTRY_ID } from '../utils/assetCountryCurrency';
import { US_COUNTRY_ID } from '../utils/assetCreateFormDefaults';
import { matchVoiceSearchCity, normalizeVoiceText, removeVoicePhrase, stripHebrewPrefixLetters } from './matchVoiceSearchCity';
import { matchVoiceSearchStreet, stripStreetMatch } from './matchVoiceSearchStreet';
import {
  formatVoiceCitySuggestionLabel,
  pickConfidentVoiceCitySuggestion,
  suggestVoiceSearchCities,
  voiceEditDistance,
  type VoiceCitySuggestion,
} from './suggestVoiceSearchCities';
import {
  emptyAssistantSchedule,
  parseAssistantScheduleFromUtterance,
  type AssistantSchedule,
} from './assistantSchedule';
import { parseAssistantWhen, stripAssistantWhenPhrases } from './parseAssistantWhen';
import { parseAssistantOpenWeekday } from './parseAssistantOpenWeekday';
import { parseAssistantOpen24h } from './parseAssistantOpen24h';
import { parseAssistantFavoritesScope } from './parseAssistantFavoritesScope';
import { parseAssistantAmenity } from './parseAssistantAmenity';
import type { AssistantPriceConstraint } from './assistantPriceConstraint';
import { parseAssistantPrice } from './assistantPriceConstraint';
import { parseAssistantPriceSort, type AssistantPriceSort } from './parseAssistantPriceSort';
import { parseAssistantProximity } from './parseAssistantProximity';

export type AssistantIntentKind =
  | 'search'
  | 'navigate'
  | 'faq'
  | 'book'
  | 'clarifyPlace'
  | 'unknown';

export type AssistantClarifyPlaceKind = 'citySuggestions' | 'streetOrCity';

export type AssistantNavigateTarget =
  | 'home'
  | 'search'
  | 'favorites'
  | 'messages'
  | 'profile'
  | 'assets'
  | 'bookings'
  | 'auth';

export type AssistantFaqTopic =
  | 'businessLounge'
  | 'becomeOwner'
  | 'howBookingWorks'
  | 'voiceHelp';

export type AssistantIntent =
  | {
      kind: 'search';
      keyword: string;
      assetTypeLabel: string | null;
      city: string | null;
      /** Numeric country id when the utterance names a country, or an Israeli city. */
      countryId: number | null;
      /** Catalog street name when the utterance names a street in the parsed city. */
      street: string | null;
      /** Street label for chips; may follow spoken token order. */
      streetDisplay: string | null;
      /** Local calendar day `YYYY-MM-DD` from today/tomorrow/weekday. */
      whenDate: string | null;
      /** Soft month window `YYYY-MM` (“בחודש דצמבר”) — any day that month. */
      whenMonth?: string | null;
      /** Clock time `HH:mm` (24h) when spoken. */
      whenTime: string | null;
      /** Spoken “open 24 hours” / פתוח 24 שעות. */
      open24h: boolean;
      /** Spoken amenities (“עם מטבח”) — hard facility-icon refine (not title keyword). */
      amenityIcons?: string[] | null;
      /** Hourly/daily price cap: less / more / equals. */
      price?: AssistantPriceConstraint | null;
      /** “Cheapest” / “most expensive” — orders the current results by price. */
      priceSort?: AssistantPriceSort | null;
      /**
       * “Closest to this street” — sort current results by distance to the street;
       * not a hard address filter (`street` stays for “on this street only”).
       */
      proximityStreet?: string | null;
      proximityStreetDisplay?: string | null;
      /**
       * Recurring booking request (weekly weekday + time + duration + until).
       * Prefer BE `browse.schedule`; local parse is an overlay only.
       */
      schedule?: AssistantSchedule | null;
      /** Leftover place name after type/city/street/when were removed. */
      venue: string | null;
      /** Explicit “all offices / כל המשרדים” — match lounge, open space, conference too. */
      officeFamily: boolean;
      /** Backend turn ids from the last search; a city follow-up can narrow this set. */
      assetIds?: string[] | null;
      /** “במועדפים שלי” / in my favorites — restrict to saved favourites. */
      favoritesOnly?: boolean;
      raw: string;
    }
  | {
      kind: 'navigate';
      target: AssistantNavigateTarget;
      raw: string;
    }
  | {
      kind: 'faq';
      topic: AssistantFaqTopic;
      raw: string;
    }
  | {
      kind: 'book';
      /** Optional asset name/keyword; null means “current / selected asset”. */
      assetQuery: string | null;
      raw: string;
      /** Requires explicit UI confirm before opening booking. */
      needsConfirm: true;
    }
  | {
      kind: 'clearFavorites';
      raw: string;
    }
  | {
      kind: 'clarifyPlace';
      raw: string;
      clarify: AssistantClarifyPlaceKind;
      /** Spoken leftover that was ambiguous (“ramadan”). */
      heard: string;
      suggestions: VoiceCitySuggestion[];
      /** Partial search to resume after the user picks a place. */
      pendingSearch: AssistantSearchIntent;
    }
  | {
      kind: 'unknown';
      raw: string;
    };

export type AssistantSearchIntent = Extract<AssistantIntent, { kind: 'search' }>;

export type ParseAssistantIntentOptions = {
  /** Previous search city, so a follow-up can match a street without repeating the city. */
  fallbackCity?: string | null;
};

const TYPE_ALIASES: Array<{ label: string; patterns: RegExp[] }> = BROWSE_FILTER_ASSET_TYPE_NAMES.map(
  (label) => {
    const phrases = browseSpokenPhrasesForType(label)
      .filter((phrase) => phrase.trim())
      .sort((a, b) => b.length - a.length);
    const patterns = phrases.map((phrase) =>
      /[a-z]/i.test(phrase)
        ? new RegExp(`\\b${escapeRegExp(phrase)}\\b`, 'i')
        : new RegExp(escapeRegExp(phrase), 'i'),
    );
    return { label, patterns };
  },
);

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function stripCommandPrefixes(raw: string): string {
  return raw
    .trim()
    // “אני רוצה/מחפש …” — strip the whole lead. Bare “אני ” alone leaves “רוצה” as a
    // fake venue keyword and empties catalog results (listSearch=רוצה).
    .replace(/^(אני\s+)?(רוצה|רוצים|רוצה\s+ש|צריך|צריכה|מחפש|מחפשת|מחפשים)\s+/i, '')
    .replace(
      /^(please\s+)?((i('m| am)?|i'm)\s+)?(want|need|find|search|look(?:ing)?\s+for|show\s+me|show|sort|חפש|מצא|תמצא|מתצא|תמצאי|תראה|תראי|הראה|הצג|תציג|תציגי|תמיין|מיין)\s+(?:לי\s+)?/i,
      '',
    )
    .replace(/^(for\s+|me\s+|את\s+|אני\s+)/i, '')
    .trim();
}

function matchAssetType(text: string): { label: string; phrase: string } | null {
  for (const entry of TYPE_ALIASES) {
    for (const pattern of entry.patterns) {
      const matched = text.match(pattern);
      if (matched?.[0]) {
        return { label: entry.label, phrase: matched[0] };
      }
    }
  }
  return null;
}

export { isGenericOfficeSearchPhrase };

function matchCity(
  text: string,
  preferStreetInCity?: string | null,
): { city: string; phrase: string } | null {
  const hit = matchVoiceSearchCity(text, { preferStreetInCity });
  if (!hit) {
    return null;
  }
  return { city: hit.cityHe, phrase: hit.matchedPhrase };
}

const COUNTRY_PHRASES: Array<{ phrase: string; id: number }> = [
  { phrase: 'state of israel', id: ISRAEL_COUNTRY_ID },
  { phrase: 'united states of america', id: US_COUNTRY_ID },
  { phrase: 'united states', id: US_COUNTRY_ID },
  { phrase: 'ארצות הברית', id: US_COUNTRY_ID },
  { phrase: 'israel', id: ISRAEL_COUNTRY_ID },
  { phrase: 'ישראל', id: ISRAEL_COUNTRY_ID },
  { phrase: 'usa', id: US_COUNTRY_ID },
].sort((a, b) => b.phrase.length - a.phrase.length);

function matchCountry(text: string): { countryId: number; phrase: string } | null {
  const hay = normalizeVoiceText(text);
  if (!hay) {
    return null;
  }
  for (const entry of COUNTRY_PHRASES) {
    const needle = normalizeVoiceText(entry.phrase);
    if (!needle) {
      continue;
    }
    const found = /[a-z]/i.test(needle)
      ? new RegExp(`(?:^|\\s)${escapeRegExp(needle)}(?:\\s|$)`, 'i').test(hay)
      : hay.includes(needle);
    if (found) {
      return { countryId: entry.id, phrase: entry.phrase };
    }
  }
  return null;
}

const FILLER_PHRASES = [
  'look for',
  'search for',
  'find me',
  'show me',
  'i want',
  'i need',
  'i am looking for',
  "i'm looking for",
  'im looking for',
  'looking for',
  'please',
  'only',
  'just',
  'also',
  'and also',
  'as well',
  'plus',
  'too',
  'וגם עם',
  'וגם',
  'גם עם',
  'גם',
  'find',
  'search',
  'show',
  'near',
  'around',
  'in',
  'at',
  'for',
  'a',
  'an',
  'the',
  'me',
  'is there any',
  'are there any',
  'how many are there',
  'how many',
  'is there',
  'are there',
  'do you have',
  'have you got',
  'any',
  'חפש לי',
  'מצא לי',
  'אני מחפש',
  'אני מחפשת',
  'אני מחפשים',
  'אני רוצה',
  'אני צריך',
  'אני צריכה',
  'בבקשה',
  'האם יש',
  'יש לכם',
  'יש לך',
  'יש שם',
  'כמה יש',
  'כמה',
  'האם',
  'יש',
  'חפש',
  'מצא לי',
  'תמצא לי',
  'מתצא לי',
  'תמתא לי',
  'מתמא לי',
  'אני מחפש',
  'אני מחפשת',
  'אני מחפשים',
  'אני רוצה',
  'בבקשה',
  'חפש',
  'מצא',
  'תמצא',
  'מתצא',
  'תמתא',
  'מתמא',
  'מחפש',
  'מחפשת',
  'מחפשים',
  'רוצה',
  'רוצים',
  'להזמין',
  'הזמן',
  'תזמין',
  'מזמין',
  'to book',
  'book a',
  'book an',
  'reserve a',
  'reserve an',
  'book',
  'reserve',
  'want',
  'needs',
  'need',
  'אני',
  'צריך',
  'צריכה',
  'לשעתיים',
  'שעתיים',
  'לשלוש שעות',
  'לסוף שנת',
  'לסוף השנה',
  'סוף שנת',
  'סוף השנה',
  'until the end of the year',
  'until the end of',
  'end of the year',
  'end of year',
  'לתאריך',
  'בתאריך',
  'תאריך',
  'on the date',
  'on date',
  'for the date',
  'for date',
  'רק',
  'מתוך אלה',
  'מתוך התוצאות',
  'בתוצאות',
  'צמצם',
  'סנן',
  'ליד',
  'באזור',
  'קרוב ל',
  'את',
  'של',
  'לי',
  'נכסים',
  'נכס',
  'מקומות',
  'מקום',
  'an asset',
  'assets',
  'asset',
  'listings',
  'listing',
  'properties',
  'property',
  'places',
  'place',
  'spaces',
  'space',
  // "All assets in X" — no type or name filter.
  'כל הנכסים',
  'כל המקומות',
  'כל החללים',
  'כל האפשרויות',
  'הנכסים',
  'המקומות',
  'החללים',
  'הכל',
  'all the assets',
  'all assets',
  'all listings',
  'all places',
  'all spaces',
  'all properties',
  'all of them',
  'everything',
  // Availability questions — the date/time carries the filter, these words do not.
  'יש זמינות',
  'יש פנוי',
  'פנוי',
  'פנויה',
  'פנויים',
  'פנויות',
  'זמין',
  'זמינה',
  'זמינים',
  'זמינות',
  'משהו',
  'any availability',
  'availability',
  'available',
  'anything',
  'vacant',
  'free',
].sort((a, b) => b.length - a.length);

function stripFillers(text: string): string {
  let out = normalizeVoiceText(text);
  let changed = true;
  while (changed && out) {
    changed = false;
    for (const filler of FILLER_PHRASES) {
      const next = removeVoicePhrase(out, filler);
      if (next !== out) {
        out = next;
        changed = true;
      }
    }
  }
  return out;
}

/** Leftover after “אני רוצה …” / “I want …” — never a place or venue name. */
function isIntentVerbLeftover(value: string): boolean {
  const n = normalizeVoiceText(value);
  return /^(רוצה|רוצים|צריך|צריכה|להזמין|הזמן|תזמין|מזמין|want|needs|need|looking|book|reserve)$/i.test(
    n,
  );
}

/** “I meant Ramat Gan” / “התכוונתי לרמת גן” — strip the correction lead. */
export function stripAssistantCorrectionLead(raw: string): string | null {
  const trimmed = raw.trim().replace(/\s+/g, ' ');
  if (!trimmed) {
    return null;
  }
  const patterns = [
    /^i\s+meant\s+/i,
    /^i\s+mean\s+/i,
    /^(?:no[,.]?\s+)?i\s+meant\s+/i,
    /^sorry[,.]?\s+(?:i\s+meant\s+)?/i,
    /^התכוונתי\s+לעיר\s+/i,
    /^התכוונתי\s+לרחוב\s+/i,
    /^התכוונתי\s+ל/i,
    /^התכוונתי\s+/i,
  ];
  for (const pattern of patterns) {
    if (pattern.test(trimmed)) {
      const rest = trimmed.replace(pattern, '').trim();
      return rest || null;
    }
  }
  return null;
}

const STREET_OR_CITY_PROBE_CITIES = [
  'תל אביב - יפו',
  'רמת גן',
  'חיפה',
  'ירושלים',
  'פתח תקווה',
  'בני ברק',
];

/** English STT leftovers (“ramadan”) — not Hebrew venue names like “בר לב”. */
function shouldFuzzyRecoverLatinPlace(value: string): boolean {
  const n = normalizeVoiceText(value);
  const latin = (n.match(/[a-z]/g) || []).length;
  const hebrew = (n.match(/[\u0590-\u05ff]/g) || []).length;
  return latin >= 3 && latin > hebrew;
}

/**
 * Only ask street-vs-city when the user explicitly said “street” / רחוב, or the
 * leftover is a known street in a probe city AND very close to that street’s spelling.
 * Do not treat random English near-misses (“ramadan”) as streets via skeleton match.
 */
function tokenLooksLikeStreetSomewhere(token: string): boolean {
  const t = normalizeVoiceText(token);
  if (t.length < 3) {
    return false;
  }
  if (/\b(street|st|road|rd|avenue|ave|רחוב|ברחוב)\b/i.test(t)) {
    return true;
  }
  for (const city of STREET_OR_CITY_PROBE_CITIES) {
    const hit = matchVoiceSearchStreet(city, t);
    if (!hit) {
      continue;
    }
    const label = normalizeVoiceText(hit.streetDisplay || hit.street);
    const compactToken = t.replace(/\s+/g, '');
    const compactLabel = label.replace(/\s+/g, '');
    // Latin leftovers vs Hebrew street labels won't be close — skip those.
    if (!/[a-z]/i.test(compactToken) || !/[a-z]/i.test(compactLabel)) {
      continue;
    }
    if (voiceEditDistance(compactToken, compactLabel) <= 2) {
      return true;
    }
  }
  return false;
}

function buildPendingSearchSkeleton(args: {
  raw: string;
  keyword: string;
  assetTypeLabel: string | null;
  officeFamily: boolean;
  whenDate: string | null;
  whenMonth: string | null;
  whenTime: string | null;
  open24h: boolean;
  amenityIcons: string[] | null;
  price: AssistantSearchIntent['price'];
  priceSort: AssistantSearchIntent['priceSort'];
  schedule: AssistantSearchIntent['schedule'];
  favoritesOnly: boolean;
}): AssistantSearchIntent {
  return {
    kind: 'search',
    keyword: args.keyword,
    assetTypeLabel: args.assetTypeLabel,
    city: null,
    countryId: null,
    street: null,
    streetDisplay: null,
    whenDate: args.whenDate,
    whenMonth: args.whenMonth,
    whenTime: args.whenTime,
    open24h: args.open24h,
    amenityIcons: args.amenityIcons,
    price: args.price,
    priceSort: args.priceSort,
    proximityStreet: null,
    proximityStreetDisplay: null,
    schedule: args.schedule,
    venue: null,
    officeFamily: args.officeFamily,
    favoritesOnly: args.favoritesOnly,
    raw: args.raw,
  };
}

function matchNavigate(text: string): AssistantNavigateTarget | null {
  const n = text.trim().toLowerCase();
  if (/^(home|בית)$/i.test(n)) return 'home';
  if (/^(search|חיפוש)$/i.test(n)) return 'search';
  if (/^(favorites|favourites|מועדפים)$/i.test(n)) return 'favorites';
  if (/^(messages|הודעות)$/i.test(n)) return 'messages';
  if (/^(profile|פרופיל)$/i.test(n)) return 'profile';
  if (/^(assets|my assets|נכסים|הנכסים שלי)$/i.test(n)) return 'assets';
  if (/^(bookings|orders|הזמנות)$/i.test(n)) return 'bookings';
  if (/^(login|sign in|התחבר|התחברות)$/i.test(n)) return 'auth';

  if (/\b(home)\b|בית/i.test(text) && /go\s+to|open|take\s+me|ללכת|עבור|פתח/i.test(text)) {
    return 'home';
  }
  if (/\b(search)\b|חיפוש/i.test(text) && /go\s+to|open|take\s+me|ללכת|עבור|פתח|^search$|^חיפוש$/i.test(text)) {
    return 'search';
  }
  if (/\b(favorites|favourites)\b|מועדפים/i.test(text)) return 'favorites';
  if (/\b(messages)\b|הודעות/i.test(text)) return 'messages';
  if (/\b(profile)\b|פרופיל/i.test(text)) return 'profile';
  if (/\b(my\s+assets|assets)\b|נכסים/i.test(text)) return 'assets';
  if (/\b(bookings|orders)\b|הזמנות/i.test(text)) return 'bookings';
  if (/\b(login|sign\s*in)\b|התחבר|התחברות/i.test(text)) return 'auth';
  if (/go\s+to\s+home|open\s+home|ללכת\s+לבית|עבור\s+לבית/i.test(text)) return 'home';
  if (/go\s+to\s+search|open\s+search|עבור\s+לחיפוש|פתח\s+חיפוש/i.test(text)) return 'search';
  return null;
}

function matchFaq(text: string): AssistantFaqTopic | null {
  if (
    /business lounge|טרקלין עסקים/i.test(text) &&
    /what|how|help|מה זה|מהו|איך|עזרה/i.test(text)
  ) {
    return 'businessLounge';
  }
  if (/become (an )?owner|asset owner|להיות בעלים|בעל נכס|להשכיר נכס/i.test(text)) {
    return 'becomeOwner';
  }
  if (/how (do i |to )?book|booking work|איך מזמינים|איך להזמין/i.test(text)) {
    return 'howBookingWorks';
  }
  if (/help|what can you do|עזרה|מה אתה יכול/i.test(text)) {
    return 'voiceHelp';
  }
  return null;
}

function matchBook(text: string): { assetQuery: string | null } | null {
  const bookEn = text.match(/^(please\s+)?(book|reserve)\s*(this|it|that)?\s*(.+)?$/i);
  const bookHe = text.match(/^(בבקשה\s+)?(הזמן|להזמין|תזמין)\s*(את\s+זה|זה)?\s*(.+)?$/i);
  const m = bookEn || bookHe;
  if (!m) {
    return null;
  }
  const rest = (m[4] ?? '').trim();
  if (!rest || /^(this|it|that|זה|את זה)$/i.test(rest)) {
    return { assetQuery: null };
  }
  // “להזמין חדר ישיבות בנתניה” is a type/place search, not “book this asset”.
  if (matchAssetType(rest) || matchCity(rest)) {
    return null;
  }
  return { assetQuery: rest };
}

function hasClearOrManageVerb(text: string): boolean {
  const n = normalizeVoiceText(text);
  return (
    /\b(clear|delete|remove|wipe|empty)\b/i.test(n) ||
    /מחק|תמחק|למחוק|נקה|תנקה|להסיר|הסר|תסיר/.test(n)
  );
}

/** Explicit find/search lead — leftover text may be a venue name. */
function hasSearchCommandLead(text: string): boolean {
  return /^(please\s+)?((i('m| am)?|i'm)\s+)?(find|search|look(?:ing)?\s+for|show\s+me|show)\b/i.test(
    text.trim(),
  )
    || /^(אני\s+)?(מחפש|מחפשת|מחפשים)\b/i.test(text.trim())
    || /^(חפש|מצא|תמצא|תמצאי|תראה|תראי|הראה|הצג|תציג|תציגי)\b/i.test(text.trim());
}

/**
 * Chores / out-of-domain asks must not become keyword catalog searches
 * (“תשטוף לי את הרצפה”, “יש לך רכב בשבילי”).
 */
function looksLikeOutOfDomainRequest(text: string): boolean {
  const raw = text.trim();
  if (!raw) {
    return false;
  }
  if (
    /תשטוף|לשטוף|תנקה לי|תסדר|תבשל|תכבס|תביא לי אוכל|wash\b|mop\b|vacuum\b|laundry|clean the floor|wash the floor/i.test(
      raw,
    )
  ) {
    return true;
  }
  // “תשטוף לי …” / “תביא את …” request shape — not every word that starts with ת
  // (e.g. business name “תחשוב עסקים” must stay a venue keyword).
  const first = raw.split(/\s+/)[0] ?? '';
  if (
    /^ת[א-ת]{3,}/.test(first)
    && !/^(תמצא|תמצאי|תראה|תראי|תציג|תציגי|תמיין|תזמין|תפתח)/.test(first)
    && /^(ת[א-ת]{3,})\s+(לי|את)(?:\s|$)/.test(raw)
  ) {
    return true;
  }
  // “Do you have a car for me?” with no asset type left after fillers.
  // Avoid `\b` after Hebrew — JS word boundaries are ASCII-only.
  if (
    (/^(יש לך|יש לכם)(?:\s|$)/.test(raw)
      || /^(do you have|have you got|can you|could you|will you|would you)\b/i.test(raw))
    && !hasSearchCommandLead(raw)
  ) {
    return true;
  }
  return false;
}

/**
 * Interrogative words (grammatical class — not a phrase blacklist).
 * Soft copulas like “יש” / “is” are omitted: they appear in real availability asks.
 */
function hasInterrogativeWord(text: string): boolean {
  const n = normalizeVoiceText(text);
  if (!n) {
    return false;
  }
  // Hebrew interrogatives (word-start; avoid ASCII \b with Hebrew).
  if (/(?:^|\s)(מה|מי|למה|מדוע|איך|כיצד|מתי|איפה|האם)(?:\s|$)/.test(n)) {
    return true;
  }
  if (/(?:^|\s)(what|what's|whats|who|why|how|when|where|which)(?:\s|$)/i.test(n)) {
    return true;
  }
  return false;
}

function hasAvailabilityAskCue(text: string): boolean {
  return /פנוי|פנויה|פנויים|פנויות|זמין|זמינה|זמינים|זמינות|available|availability|vacant|\bfree\b/i.test(
    text,
  );
}

/**
 * Venue-only / keyword-only searches (e.g. onobiz) — not free-form chat.
 * A search requires either structured cues (handled by the caller), an explicit
 * find/search lead, or a short name-like token with no question/chat shape.
 */
function isPlausibleKeywordOnlySearch(keyword: string, raw: string): boolean {
  const k = keyword.trim();
  if (k.length < 2) {
    return false;
  }
  if (looksLikeOutOfDomainRequest(raw)) {
    return false;
  }
  const words = k.split(/\s+/).filter(Boolean);
  if (hasSearchCommandLead(raw)) {
    // “תמצא לי X” / “find X” — leftover may be a multi-word venue name.
    return words.length >= 1 && words.length <= 6;
  }
  // No search command and no structured fields: only short name-like queries.
  if (hasInterrogativeWord(raw) || /[?؟]/.test(raw) || /^(יש|האם|is there|are there|do you)\b/i.test(raw.trim())) {
    return false;
  }
  return words.length >= 1 && words.length <= 2;
}

/** “Delete all my favorites” / “תמחק לי את כל המועדפים”. */
export function matchClearFavorites(text: string): boolean {
  const n = normalizeVoiceText(text);
  if (!n) {
    return false;
  }
  const hasFavorites =
    /\b(favorites|favourites|favorite|favourite)\b/i.test(n) || /מועדפ/.test(n);
  if (!hasFavorites) {
    return false;
  }
  if (!hasClearOrManageVerb(n)) {
    return false;
  }
  // Prefer explicit “all / כל” so “remove this favorite” stays for later.
  const allScope =
    /\b(all|every|entire)\b/i.test(n) ||
    /את\s+כל|כל\s+ה|את\s+הכל|\bכל\b/.test(n) ||
    /את\s+המועדפים/.test(n);
  return allScope;
}

function citySearchToken(city: string): string {
  return city.split(/\s*[-–]\s*/)[0]?.trim() || city.trim();
}

const LOCATION_AREA_PREFIXES = [
  'פארק התעשיות',
  'פארק התעשיה',
  'פארק תעשיות',
  'פארק תעשייה',
  'פארק תעשיה',
  'אזור התעשייה',
  'אזור התעשיה',
  'אזור תעשייה',
  'אזור תעשיה',
  'industrial park',
  'industrial zone',
  'business park',
].sort((a, b) => b.length - a.length);

/** Drop “industrial park / פארק התעשיות” so “בר לב” can match as the place name. */
function stripLocationAreaPrefixes(text: string): string {
  let out = normalizeVoiceText(text);
  for (const phrase of LOCATION_AREA_PREFIXES) {
    const next = removeVoicePhrase(out, phrase);
    if (next !== out) {
      out = next;
    }
  }
  return out.replace(/^(פארק|אזור|park|zone)\s+/i, '').trim();
}

function remainderHasCity(remainder: string, city: string): boolean {
  const hay = normalizeVoiceText(remainder);
  const needle = normalizeVoiceText(city);
  if (!hay || !needle) {
    return false;
  }
  return hay.includes(needle) || needle.includes(hay);
}

function isHebrewPhrase(value: string): boolean {
  return /[\u0590-\u05ff]/.test(value);
}

function buildSearchKeyword(parts: {
  stripped: string;
  remainder: string;
  assetTypeLabel: string | null;
  typePhrase: string | null;
  city: string | null;
}): string {
  const remainder = stripHebrewPrefixLetters(parts.remainder);
  if (remainder.length >= 3) {
    if (parts.city && !remainderHasCity(remainder, parts.city)) {
      return `${remainder} ${citySearchToken(parts.city)}`.trim();
    }
    return remainder;
  }
  const typeToken =
    parts.typePhrase && isHebrewPhrase(parts.typePhrase)
      ? parts.typePhrase
      : parts.assetTypeLabel;
  const tokens: string[] = [];
  if (typeToken) {
    tokens.push(typeToken);
  }
  if (parts.city) {
    tokens.push(citySearchToken(parts.city));
  }
  if (tokens.length > 0) {
    return tokens.join(' ').trim();
  }
  return stripHebrewPrefixLetters(parts.stripped) || parts.stripped.replace(/\s+/g, ' ').trim();
}

/** Deterministic HE/EN intent parser for the voice/chat assistant. */
export function parseAssistantIntent(
  rawInput: string,
  options: ParseAssistantIntentOptions = {},
): AssistantIntent {
  const raw = rawInput.trim().replace(/\s+/g, ' ');
  if (!raw) {
    return { kind: 'unknown', raw };
  }

  const faq = matchFaq(raw);
  if (faq && /^(what|how|help|מה|איך|עזרה)/i.test(raw)) {
    return { kind: 'faq', topic: faq, raw };
  }

  const book = matchBook(raw);
  if (book) {
    return { kind: 'book', assetQuery: book.assetQuery, raw, needsConfirm: true };
  }

  if (matchClearFavorites(raw)) {
    return { kind: 'clearFavorites', raw };
  }

  // “תנקה את כל המדפים” (typo) must not become a venue search → empty results.
  if (hasClearOrManageVerb(raw)) {
    return { kind: 'unknown', raw };
  }

  const navigatePrefix = /^(go\s+to|open|take\s+me\s+to|ללכת|עבור|פתח)/i.test(raw);
  const navigate = navigatePrefix || /^(home|search|favorites|messages|profile|assets|bookings|login|בית|חיפוש|מועדפים|הודעות|פרופיל|נכסים|הזמנות|התחבר)/i.test(raw)
    ? matchNavigate(raw)
    : null;
  if (navigate && (navigatePrefix || /^(home|search|favorites|messages|profile|assets|bookings|login|בית|חיפוש|מועדפים|הודעות|פרופיל|נכסים|הזמנות|התחבר)/i.test(raw))) {
    return { kind: 'navigate', target: navigate, raw };
  }

  if (faq) {
    return { kind: 'faq', topic: faq, raw };
  }

  const stripped = stripCommandPrefixes(raw);
  const typeHit = matchAssetType(stripped) ?? matchAssetType(raw);
  let remainder = normalizeVoiceText(stripped);
  if (typeHit) {
    remainder = removeVoicePhrase(remainder, typeHit.phrase);
  }
  remainder = stripLocationAreaPrefixes(remainder);
  const fallbackCity = options.fallbackCity?.trim() || null;
  const cityHit = matchCity(remainder, fallbackCity) ?? matchCity(stripped, fallbackCity) ?? matchCity(raw, fallbackCity);
  if (cityHit) {
    remainder = removeVoicePhrase(remainder, cityHit.phrase);
  }
  const countryHit = matchCountry(remainder) ?? matchCountry(stripped) ?? matchCountry(raw);
  if (countryHit) {
    remainder = removeVoicePhrase(remainder, countryHit.phrase);
  }
  // Favorites scope before venue leftovers (“במועדפים שלי” / typo במעודפים).
  const favoritesScopeHit = parseAssistantFavoritesScope(remainder);
  remainder = favoritesScopeHit.remainder;
  // “שפתוח/שסגור בשבת” before when/open24h — otherwise “בשבת” becomes whenDate and leftovers a venue.
  const openWeekdayHit = parseAssistantOpenWeekday(remainder);
  remainder = openWeekdayHit.remainder;
  const when = parseAssistantWhen(remainder);
  remainder = stripAssistantWhenPhrases(remainder, when);
  const priceHit = parseAssistantPrice(remainder);
  remainder = priceHit.remainder;
  const priceSortHit = parseAssistantPriceSort(remainder);
  remainder = priceSortHit.remainder;
  const proximityHit = parseAssistantProximity(remainder);
  remainder = proximityHit.remainder;
  const open24hHit = parseAssistantOpen24h(remainder);
  remainder = open24hHit.remainder;
  const amenityHit = parseAssistantAmenity(remainder);
  remainder = amenityHit.remainder;
  const amenityIcons = amenityHit.icons.length > 0 ? amenityHit.icons : null;
  const cityHitCity = cityHit?.city ?? null;
  // Drop “באזור / near / around” before street match — leftover “באזור” falsely
  // matches Tel Aviv street ביצרון and then poisons Haifa follow-ups.
  // Also drop booking verbs — “להזמין” skeleton-matched Netanya street זמנהוף.
  for (const filler of [
    'באזור',
    'אזור',
    'near',
    'around',
    'ליד',
    'קרוב ל',
    'in the area of',
    'in the area',
    'להזמין',
    'הזמן',
    'תזמין',
    'מזמין',
    'to book',
    'book a',
    'book an',
    'reserve a',
    'reserve an',
    'book',
    'reserve',
  ]) {
    remainder = removeVoicePhrase(remainder, filler);
  }
  const streetHit = matchVoiceSearchStreet(cityHitCity ?? fallbackCity, remainder);
  if (streetHit) {
    remainder = stripStreetMatch(remainder, streetHit);
  }
  remainder = stripLocationAreaPrefixes(stripHebrewPrefixLetters(stripFillers(remainder)));
  const assetTypeLabel = typeHit?.label ?? null;
  const city = cityHitCity ?? (streetHit ? fallbackCity : null);
  const countryId = city ? ISRAEL_COUNTRY_ID : countryHit?.countryId ?? null;
  // Proximity + street → sort-by-distance; hard street filter only without proximity.
  const proximityStreet =
    proximityHit.proximity && streetHit ? streetHit.street : null;
  const proximityStreetDisplay =
    proximityHit.proximity && streetHit
      ? streetHit.streetDisplay ?? streetHit.street
      : null;
  const street = proximityStreet ? null : streetHit?.street ?? null;
  const streetDisplay = proximityStreet ? null : streetHit?.streetDisplay ?? street;
  let venue =
    remainder.length >= 3 && (!city || !remainderHasCity(remainder, city)) ? remainder : null;
  // Intent verbs left after stripping “אני …” (“רוצה”, “want”) are not venue names.
  if (venue && isIntentVerbLeftover(venue)) {
    venue = null;
    remainder = '';
  }
  let schedule = parseAssistantScheduleFromUtterance(raw);
  if (openWeekdayHit.weekdays.length > 0 || openWeekdayHit.closedWeekdays.length > 0) {
    const base = schedule ?? emptyAssistantSchedule();
    const closed_weekdays = Array.from(
      new Set([...(base.closed_weekdays ?? []), ...openWeekdayHit.closedWeekdays]),
    );
    const closedSet = new Set(closed_weekdays);
    const weekdays = Array.from(new Set([...base.weekdays, ...openWeekdayHit.weekdays])).filter(
      (day) => !closedSet.has(day),
    );
    schedule = {
      frequency: base.frequency ?? 'weekly',
      weekdays,
      closed_weekdays,
      start_time: base.start_time,
      duration_minutes: base.duration_minutes,
      until_date: base.until_date,
    };
  }
  if (when.durationMinutes != null && when.durationMinutes > 0) {
    schedule = {
      frequency: schedule?.frequency ?? null,
      weekdays: schedule?.weekdays ?? [],
      closed_weekdays: schedule?.closed_weekdays ?? [],
      start_time: when.time ?? schedule?.start_time ?? null,
      duration_minutes: when.durationMinutes,
      until_date: schedule?.until_date ?? null,
    };
  }
  // One-off “ביום שלישי הקרוב” already sets whenDate. Do not also hard-filter
  // opening_times by that weekday (list hours are often incomplete → empty).
  // Explicit “שפתוח/שסגור בשלישי” keeps openWeekdayHit weekdays.
  if (
    when.date &&
    schedule &&
    openWeekdayHit.weekdays.length === 0 &&
    openWeekdayHit.closedWeekdays.length === 0
  ) {
    const hasSlot = Boolean(
      schedule.start_time || schedule.duration_minutes || schedule.until_date,
    );
    schedule = hasSlot
      ? {
          frequency: null,
          weekdays: [],
          closed_weekdays: [],
          start_time: schedule.start_time,
          duration_minutes: schedule.duration_minutes,
          until_date: schedule.until_date,
        }
      : null;
  }
  // Prefer schedule until_date over a year mis-read as ₪.
  const price =
    schedule?.until_date &&
    priceHit.constraint &&
    priceHit.constraint.amount >= 1900 &&
    priceHit.constraint.amount <= 2100
      ? null
      : priceHit.constraint;
  const keyword = buildSearchKeyword({
    stripped,
    remainder,
    assetTypeLabel,
    typePhrase: typeHit?.phrase ?? null,
    city,
  });

  /** Type / place / price / … — enough to search even without “find”. */
  const hasStrongSearchSignal = Boolean(
    assetTypeLabel
      || city
      || street
      || proximityStreet
      || open24hHit.matched
      || amenityIcons
      || price
      || priceSortHit.sort
      || schedule
      || favoritesScopeHit.matched
      || countryId != null
      || hasSearchCommandLead(raw),
  );
  /** Date/time alone is weak (chat like “how are you today” can match “today”). */
  const hasStructuredSearch = Boolean(
    hasStrongSearchSignal || when.date || when.time || when.month,
  );
  const keywordOnlyOk =
    !hasStructuredSearch && isPlausibleKeywordOnlySearch(keyword || remainder, raw);

  // Question/chat shape with no real search/command signal → unknown, not empty results.
  // Exception: availability + date/time (“יש פנוי לראשון?”) is a valid (often follow-up) search.
  if (
    hasInterrogativeWord(raw)
    && !hasStrongSearchSignal
    && !keywordOnlyOk
    && !(hasAvailabilityAskCue(raw) && (when.date || when.time || when.month))
  ) {
    return { kind: 'unknown', raw };
  }

  if (hasStructuredSearch || keywordOnlyOk) {
    let resolvedCity = city;
    let resolvedCountryId = countryId;
    let resolvedVenue = venue;
    let resolvedStreet = street;
    let resolvedStreetDisplay = streetDisplay;
    // Bare “משרד / office” → Private Office only. Family expand needs “כל המשרדים / all offices”.
    const officeFamily = isOfficeFamilySearchPhrase(raw);
    const whenTime = when.time ?? schedule?.start_time ?? null;
    const searchKeyword = keyword || stripped || raw;

    // Unmatched Latin leftover (“ramadan”) — fuzzy-match Israel cities.
    // Keep Hebrew venue names (“בר לב”) as keyword search; don’t invent a city.
    if (!resolvedCity && resolvedVenue && shouldFuzzyRecoverLatinPlace(resolvedVenue)) {
      const suggestions = suggestVoiceSearchCities(resolvedVenue, { limit: 5 });
      const confident = pickConfidentVoiceCitySuggestion(suggestions);
      const streetAmbiguous =
        suggestions.length > 0 && tokenLooksLikeStreetSomewhere(resolvedVenue);

      if (confident && !streetAmbiguous) {
        resolvedCity = confident.cityHe;
        resolvedCountryId = ISRAEL_COUNTRY_ID;
        resolvedVenue = null;
      } else if (suggestions.length > 0) {
        return {
          kind: 'clarifyPlace',
          raw,
          clarify: streetAmbiguous ? 'streetOrCity' : 'citySuggestions',
          heard: resolvedVenue,
          suggestions,
          pendingSearch: buildPendingSearchSkeleton({
            raw,
            keyword: searchKeyword,
            assetTypeLabel,
            officeFamily,
            whenDate: when.date,
            whenMonth: when.month,
            whenTime,
            open24h: open24hHit.matched,
            amenityIcons,
            price,
            priceSort: priceSortHit.sort,
            schedule,
            favoritesOnly: favoritesScopeHit.matched,
          }),
        };
      }
    }

    return {
      kind: 'search',
      keyword: searchKeyword,
      assetTypeLabel,
      city: resolvedCity,
      countryId: resolvedCountryId,
      street: resolvedStreet,
      streetDisplay: resolvedStreetDisplay,
      whenDate: when.date,
      whenMonth: when.month,
      whenTime,
      open24h: open24hHit.matched,
      amenityIcons,
      price,
      priceSort: priceSortHit.sort,
      proximityStreet,
      proximityStreetDisplay,
      schedule,
      venue: resolvedVenue,
      officeFamily,
      favoritesOnly: favoritesScopeHit.matched,
      raw,
    };
  }

  // Place-only Latin correction / typo (“ramadan”, “I meant Ramada Ghana”).
  if (!hasStructuredSearch) {
    const placeQuery = venue || stripFillers(normalizeVoiceText(stripped)) || '';
    if (placeQuery.length >= 3 && shouldFuzzyRecoverLatinPlace(placeQuery)) {
      const suggestions = suggestVoiceSearchCities(placeQuery, { limit: 5 });
      const confident = pickConfidentVoiceCitySuggestion(suggestions);
      if (confident) {
        return {
          kind: 'search',
          keyword: confident.cityHe,
          assetTypeLabel: null,
          city: confident.cityHe,
          countryId: ISRAEL_COUNTRY_ID,
          street: null,
          streetDisplay: null,
          whenDate: null,
          whenMonth: null,
          whenTime: null,
          open24h: false,
          amenityIcons: null,
          price: null,
          priceSort: null,
          proximityStreet: null,
          proximityStreetDisplay: null,
          schedule: null,
          venue: null,
          officeFamily: false,
          favoritesOnly: false,
          raw,
        };
      }
      if (suggestions.length > 0) {
        return {
          kind: 'clarifyPlace',
          raw,
          clarify: 'citySuggestions',
          heard: placeQuery,
          suggestions,
          pendingSearch: buildPendingSearchSkeleton({
            raw,
            keyword: placeQuery,
            assetTypeLabel: null,
            officeFamily: false,
            whenDate: null,
            whenMonth: null,
            whenTime: null,
            open24h: false,
            amenityIcons: null,
            price: null,
            priceSort: null,
            schedule: null,
            favoritesOnly: false,
          }),
        };
      }
    }
  }

  if (navigate) {
    return { kind: 'navigate', target: navigate, raw };
  }

  return { kind: 'unknown', raw };
}

/** Join suggestion labels for clarify copy (“Ramat Gan, Ramat HaSharon”). */
export function formatAssistantCitySuggestionList(
  suggestions: VoiceCitySuggestion[],
  language: 'he' | 'en',
  limit = 3,
): string {
  return suggestions
    .slice(0, limit)
    .map((item) => formatVoiceCitySuggestionLabel(item, language))
    .join(language === 'he' ? ', ' : ', ');
}
